import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { map } from 'rxjs/operators';
import { SNACKBAR_APPEARANCE, SnackbarType } from './snackbar.model';

const MOBILE_LAYOUT_QUERY = '(width < 48rem)';

@Component({
  selector: 'fc-snackbar',
  imports: [IconComponent],
  host: {
    class:
      'fc-anim-slide-up-in flex w-full items-center gap-12 rounded-xl bg-white px-12 py-8 shadow-xl md:w-420 md:rounded-2xl md:px-16 md:py-12',
    '[class.fc-anim-slide-down-out]': 'isLeaving()',
    '(animationend)': 'onAnimationEnd($event)',
  },
  template: `
    <div
      class="flex size-32 shrink-0 items-center justify-center rounded-full md:size-40"
      [class]="appearance().iconBackgroundClass"
    >
      <fc-icon
        [icon]="appearance().icon"
        [color]="appearance().iconColor"
        [size]="isMobileLayout() ? 16 : 20"
      />
    </div>
    <div
      class="min-w-0 flex-1 break-words text-sm font-medium text-grey-900 md:text-base"
    >
      <ng-content />
      {{ message() }}
    </div>
    @if (closeable()) {
      <button
        type="button"
        aria-label="Close"
        class="flex shrink-0 cursor-pointer rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-secondary-500"
        (click)="dismiss()"
      >
        <fc-icon icon="icon-close" color="secondary400" [size]="24" />
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnackbarComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly message = input<string>();
  readonly type = input<SnackbarType>('success');
  readonly closeable = input(false);
  readonly dismissed = output<void>();

  protected readonly isLeaving = signal(false);
  protected readonly appearance = computed(
    () => SNACKBAR_APPEARANCE[this.type()],
  );
  protected readonly isMobileLayout = toSignal(
    this.breakpointObserver
      .observe(MOBILE_LAYOUT_QUERY)
      .pipe(map((state) => state.matches)),
    { initialValue: this.breakpointObserver.isMatched(MOBILE_LAYOUT_QUERY) },
  );

  dismiss(): void {
    this.isLeaving.set(true);
  }

  protected onAnimationEnd(event: AnimationEvent): void {
    if (event.animationName === 'fc-slide-down-out') {
      this.dismissed.emit();
    }
  }
}
